import { ContentTypes, OperationTypes, IOPS, zeroIOPS } from "./block";

export enum TrafficPatterns {
    INCREASE_BY_LEVEL = 'INCREASE_BY_LEVEL',
    INCREASE_BY_LEVEL_WITH_BACK_PRESSURE = 'INCREASE_BY_LEVEL_WITH_BACK_PRESSURE',
    STEADY = 'STEADY',
    STEADY_WAVE = 'STEADY_WAVE',
    RAMP = 'RAMP',
    PERIODIC = 'PERIODIC',
}

export const TrafficPatternNames = {
    [TrafficPatterns.INCREASE_BY_LEVEL]: 'Increase by Level',
    [TrafficPatterns.INCREASE_BY_LEVEL_WITH_BACK_PRESSURE]: 'Increase by Level with Back Pressure',
    [TrafficPatterns.STEADY]: 'Steady',
    [TrafficPatterns.STEADY_WAVE]: 'Steady Wave',
    [TrafficPatterns.RAMP]: 'Ramp',
    [TrafficPatterns.PERIODIC]: 'Periodic',
}

export const TrafficPatternShortNames = {
    [TrafficPatterns.INCREASE_BY_LEVEL]: 'Inc.',
    [TrafficPatterns.INCREASE_BY_LEVEL_WITH_BACK_PRESSURE]: 'Inc. w/ BP',
    [TrafficPatterns.STEADY]: 'Steady',
    [TrafficPatterns.STEADY_WAVE]: 'Wave',
    [TrafficPatterns.RAMP]: 'Ramp',
    [TrafficPatterns.PERIODIC]: 'Periodic',
}

export const PERIODIC_INTERVAL = 60;
export const PERIODIC_RAMP = 15;
export const PERIODIC_HOLD = 20;
export const RAMP_DURATION = 60;

export function getPeriodicTPS(time: number, minTPS: number, maxTPS: number): number {
    const t = time % PERIODIC_INTERVAL;
    
    // Phase 1: Ramp up from minTPS to maxTPS
    if (t < PERIODIC_RAMP) {
        return minTPS + (maxTPS - minTPS) * (t / PERIODIC_RAMP);
    }
    
    // Phase 2: Stay at maxTPS
    if (t < PERIODIC_RAMP + PERIODIC_HOLD) {
        return maxTPS;
    }
    
    // Phase 3: Ramp down from maxTPS to minTPS
    if (t < PERIODIC_RAMP + PERIODIC_HOLD + PERIODIC_RAMP) {
        const rampDownTime = t - (PERIODIC_RAMP + PERIODIC_HOLD);
        return maxTPS - (maxTPS - minTPS) * (rampDownTime / PERIODIC_RAMP);
    }
    
    // Phase 4: Stay at minTPS
    return minTPS;
}



const MIN_IOPS_SCALE = 0.5;
const MAX_IOPS_SCALE = 2.0;

const IOPS_SCALE_BY_LEVEL = [
    { totalIOPS: 1000, minScale: 0.9, maxScale: 1.2 },
    { totalIOPS: 10000, minScale: 0.95, maxScale: 1.1 },
    { totalIOPS: 100000, minScale: 0.98, maxScale: 1.05 },
    { totalIOPS: 1000000, minScale: 0.99, maxScale: 1.02 },
]

const IOPS_SCALE_BY_LEVEL_STEADY_WAVE = [
    { totalIOPS: 1000, minScale: 0.9, maxScale: 1.1 },
    { totalIOPS: 10000, minScale: 0.95, maxScale: 1.05 },
    { totalIOPS: 100000, minScale: 0.98, maxScale: 1.02 },
    { totalIOPS: 1000000, minScale: 0.99, maxScale: 1.01 },
]

//Computes a random fractional number between two values
export function getRandomFractionalNumber(min: number, max: number) {
    return Math.random() * (max - min) + min;
}


//Gets the max and min IOPS scale for a given total IOPS
export function getNextIOPSScaleIncreaseLevel(totalIOPS: number): number {
    let minScale = MIN_IOPS_SCALE;
    let maxScale = MAX_IOPS_SCALE;
    IOPS_SCALE_BY_LEVEL.forEach((level) => {
        if (totalIOPS >= level.totalIOPS) {
            minScale = level.minScale;
            maxScale = level.maxScale;
        }
    });

    return getRandomFractionalNumber(minScale, maxScale);
}

export function getNextIOPSScaleIncreaseLevelWithBackPressure(totalIOPS: number, lastPercentSuccess: number = 100): number {
    let minScale = MIN_IOPS_SCALE;
    let maxScale = MAX_IOPS_SCALE;
    IOPS_SCALE_BY_LEVEL.forEach((level) => {
        if (totalIOPS >= level.totalIOPS) {
            minScale = level.minScale;
            maxScale = level.maxScale;
        }
    });

    //If the last cycle was not 100% successful, reverse the min and max scale
    if (lastPercentSuccess < 100) {
        const temp = minScale;
        minScale = 2 - maxScale;
        maxScale = 2 - temp;
    }

    return getRandomFractionalNumber(minScale, maxScale);
}

export function getNextIOPSScaleSteadyWave(totalIOPS: number): number {
    let minScale = MIN_IOPS_SCALE;
    let maxScale = MAX_IOPS_SCALE;
    IOPS_SCALE_BY_LEVEL_STEADY_WAVE.forEach((level) => {
        if (totalIOPS >= level.totalIOPS) {
            minScale = level.minScale;
            maxScale = level.maxScale;
        }
    });

    return getRandomFractionalNumber(minScale, maxScale);
}

export function getNextIOPSScale(pattern: TrafficPatterns, totalIOPS: number, lastPercentSuccess: number = 100): number {
    switch (pattern) {
        case TrafficPatterns.INCREASE_BY_LEVEL:
            return getNextIOPSScaleIncreaseLevel(totalIOPS);
        case TrafficPatterns.INCREASE_BY_LEVEL_WITH_BACK_PRESSURE:
            return getNextIOPSScaleIncreaseLevelWithBackPressure(totalIOPS, lastPercentSuccess);
        case TrafficPatterns.STEADY:
            return 1;
        case TrafficPatterns.STEADY_WAVE:
            return getNextIOPSScaleSteadyWave(totalIOPS);
        default:
            return 1;
    }
}

export interface TrafficRouteContentType {
    id: string;
    type: ContentTypes;
    weight: number;
    readRatio: number; // 0 to 100
}

export interface TrafficRoute {
    id: string;
    path: string;
    weight: number;
    contentTypes: TrafficRouteContentType[];
}

export type TrafficRoutes = TrafficRoute[];

export const defaultTrafficRoutes: TrafficRoutes = [
  {
    id: 'default-app-route',
    path: '/app',
    weight: 100,
    contentTypes: [
      { id: 'default-static', type: ContentTypes.STATIC, weight: 50, readRatio: 100 },
      { id: 'default-dynamic', type: ContentTypes.DYNAMIC, weight: 50, readRatio: 50 }
    ]
  }
];

export function computeIOPSFromRoutes(totalTPS: number, routes: TrafficRoute[]): IOPS {
    const iops = zeroIOPS();
    iops.total = totalTPS;

    const totalRouteWeight = routes.reduce((sum, r) => sum + r.weight, 0);
    if (totalRouteWeight === 0) return iops;

    const readContentTypeTotals = {
        [ContentTypes.STATIC]: 0,
        [ContentTypes.DYNAMIC]: 0,
        [ContentTypes.STATIC_MEDIA]: 0,
    };
    const writeContentTypeTotals = {
        [ContentTypes.STATIC]: 0,
        [ContentTypes.DYNAMIC]: 0,
        [ContentTypes.STATIC_MEDIA]: 0,
    };

    routes.forEach(route => {
        const routeTPS = totalTPS * (route.weight / totalRouteWeight);

        const totalCTWeight = route.contentTypes.reduce((sum, ct) => sum + ct.weight, 0);
        if (totalCTWeight === 0) return;

        route.contentTypes.forEach(ct => {
            const ctTPS = routeTPS * (ct.weight / totalCTWeight);

            const readTPS = ctTPS * (ct.readRatio / 100);
            const writeTPS = ctTPS * (1 - ct.readRatio / 100);

            readContentTypeTotals[ct.type] += readTPS;
            writeContentTypeTotals[ct.type] += writeTPS;
        });
    });

    let finalReadTotal = 0;
    let finalWriteTotal = 0;

    [ContentTypes.STATIC, ContentTypes.DYNAMIC, ContentTypes.STATIC_MEDIA].forEach(ct => {
        const rVal = Math.round(readContentTypeTotals[ct]);
        const wVal = Math.round(writeContentTypeTotals[ct]);

        if (iops.byOperationType[OperationTypes.READ]) {
            iops.byOperationType[OperationTypes.READ]!.byContentType[ct] = rVal;
        }
        if (iops.byOperationType[OperationTypes.WRITE]) {
            iops.byOperationType[OperationTypes.WRITE]!.byContentType[ct] = wVal;
        }

        finalReadTotal += rVal;
        finalWriteTotal += wVal;
    });

    if (iops.byOperationType[OperationTypes.READ]) {
        iops.byOperationType[OperationTypes.READ]!.total = finalReadTotal;
    }
    if (iops.byOperationType[OperationTypes.WRITE]) {
        iops.byOperationType[OperationTypes.WRITE]!.total = finalWriteTotal;
    }
    iops.total = finalReadTotal + finalWriteTotal;

    return iops;
}